/**
 * Teen Theme Selector
 * A beautiful glassmorphic modal for switching themes with live preview
 * Features instant color transitions and "Does this feel right?" confirmation
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Check, 
  Palette, 
  Sparkles,
  Moon,
  Sun,
  RefreshCw
} from 'lucide-react';
import { useTeenTheme, TEEN_THEMES, CUSTOM_PALETTES, AGE_OPTIONS } from '../contexts/TeenThemeContext';

// Theme mode indicator
const ModeIndicator = ({ mode, theme }) => (
  <div 
    className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
    style={{ 
      background: `${theme.colors.textMuted}20`,
      color: theme.colors.textSecondary,
    }}
  >
    {mode === 'dark' ? <Moon size={12} /> : <Sun size={12} />}
    {mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
  </div>
);

// Color palette preview
const PalettePreview = ({ palette, isActive, onClick, theme }) => (
  <motion.button
    onClick={onClick}
    className={`relative p-4 rounded-xl overflow-hidden transition-all ${
      isActive ? 'ring-2' : ''
    }`}
    style={{ 
      background: palette.bg,
      ringColor: isActive ? palette.primary : 'transparent',
    }}
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.98 }}
    data-testid={`palette-${palette.id}`}
  >
    {/* Color dots */}
    <div className="flex gap-2 mb-3">
      <motion.div 
        className="w-8 h-8 rounded-full shadow-lg"
        style={{ background: palette.primary }}
        layoutId={isActive ? `primary-${palette.id}` : undefined}
      />
      <motion.div 
        className="w-8 h-8 rounded-full shadow-lg"
        style={{ background: palette.accent }}
        layoutId={isActive ? `accent-${palette.id}` : undefined}
      />
    </div>
    
    {/* Name */}
    <p 
      className="text-sm font-medium"
      style={{ color: theme.mode === 'dark' ? '#fff' : '#1E293B' }}
    >
      {palette.name}
    </p>
    
    {/* Active indicator */}
    {isActive && (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
        style={{ background: palette.primary }}
      >
        <Check size={12} className="text-white" />
      </motion.div>
    )}
    
    {/* Decorative glow */}
    <div 
      className="absolute -bottom-4 -right-4 w-16 h-16 rounded-full opacity-40"
      style={{ background: `radial-gradient(circle, ${palette.accent}, transparent)` }}
    />
  </motion.button>
);

// Age bracket switcher
const AgeBracketSwitcher = ({ currentAge, onSwitch, theme }) => (
  <div className="flex gap-2 justify-center">
    {AGE_OPTIONS.map((option) => (
      <motion.button
        key={option.id}
        onClick={() => onSwitch(option.id)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          currentAge === option.id ? '' : 'opacity-60 hover:opacity-80'
        }`}
        style={{ 
          background: currentAge === option.id 
            ? `${theme.colors.primary}20` 
            : 'transparent',
          color: currentAge === option.id 
            ? theme.colors.primary 
            : theme.colors.textSecondary,
        }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        data-testid={`age-switch-${option.id}`}
      >
        {option.label}
      </motion.button>
    ))}
  </div>
);

// Main Theme Selector Modal
const TeenThemeSelector = ({ isOpen, onClose }) => {
  const { 
    ageGroup, 
    setAgeGroup,
    currentPalette, 
    updatePalette,
    theme,
    availablePalettes,
  } = useTeenTheme();
  
  const [previewAge, setPreviewAge] = useState(ageGroup);
  const [previewPalette, setPreviewPalette] = useState(currentPalette);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Get theme and palettes for preview
  const previewTheme = previewAge ? TEEN_THEMES[previewAge] : theme;
  const previewPalettes = previewAge ? CUSTOM_PALETTES[previewAge] : availablePalettes;

  // Reset preview state when opening
  useEffect(() => {
    if (isOpen) {
      setPreviewAge(ageGroup);
      setPreviewPalette(currentPalette);
      setHasChanges(false);
    }
  }, [isOpen, ageGroup, currentPalette]);

  // Track changes
  useEffect(() => {
    const ageChanged = previewAge !== ageGroup;
    const paletteChanged = previewPalette?.id !== currentPalette?.id;
    setHasChanges(ageChanged || paletteChanged);
  }, [previewAge, previewPalette, ageGroup, currentPalette]);

  const handleAgeSwitchPreview = (newAge) => {
    setPreviewAge(newAge);
    // Auto-select first palette of new age group
    const newPalettes = CUSTOM_PALETTES[newAge];
    if (newPalettes) {
      setPreviewPalette(newPalettes[0]);
    }
  };

  const handleApply = () => {
    if (previewAge !== ageGroup) {
      setAgeGroup(previewAge);
    }
    updatePalette(previewPalette);
    onClose();
  };

  const handleReset = () => {
    setPreviewAge(ageGroup);
    setPreviewPalette(currentPalette);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Backdrop */}
        <motion.div 
          className="absolute inset-0"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.95 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-md max-h-[85vh] overflow-hidden rounded-3xl"
          style={{ 
            background: previewTheme.colors.base,
            border: `1px solid ${previewTheme.colors.border}`,
          }}
          data-testid="theme-selector-modal"
        >
          {/* Animated background glow */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            <motion.div 
              className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-20"
              style={{ 
                background: `radial-gradient(circle, ${previewPalette?.primary || previewTheme.colors.primary}, transparent 70%)` 
              }}
              animate={{ 
                scale: [1, 1.2, 1],
                x: [0, 10, 0],
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div 
              className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full opacity-15"
              style={{ 
                background: `radial-gradient(circle, ${previewPalette?.accent || previewTheme.colors.accent}, transparent 70%)` 
              }}
              animate={{ 
                scale: [1, 1.3, 1],
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>

          {/* Content */}
          <div className="relative z-10 p-6 overflow-y-auto max-h-[85vh]">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${previewTheme.colors.primary}20` }}
                >
                  <Palette size={20} style={{ color: previewTheme.colors.primary }} />
                </div>
                <div>
                  <h2 
                    className="text-lg font-semibold font-outfit"
                    style={{ color: previewTheme.colors.textPrimary }}
                  >
                    Customize Your Space
                  </h2>
                  <p 
                    className="text-xs"
                    style={{ color: previewTheme.colors.textMuted }}
                  >
                    Make it feel like you
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                style={{ 
                  background: `${previewTheme.colors.textMuted}20`,
                  color: previewTheme.colors.textSecondary,
                }}
                data-testid="theme-selector-close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Current theme indicator */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p 
                  className="text-xs uppercase tracking-wider mb-1"
                  style={{ color: previewTheme.colors.textMuted }}
                >
                  Current Theme
                </p>
                <p 
                  className="font-medium font-outfit"
                  style={{ color: previewTheme.colors.textPrimary }}
                >
                  {previewTheme.name}
                </p>
              </div>
              <ModeIndicator mode={previewTheme.mode} theme={previewTheme} />
            </div>

            {/* Age bracket switcher */}
            <div className="mb-6">
              <p 
                className="text-xs uppercase tracking-wider mb-3 text-center"
                style={{ color: previewTheme.colors.textMuted }}
              >
                Age Group
              </p>
              <AgeBracketSwitcher 
                currentAge={previewAge} 
                onSwitch={handleAgeSwitchPreview}
                theme={previewTheme}
              />
            </div>

            {/* Color palettes */}
            <div className="mb-6">
              <p 
                className="text-xs uppercase tracking-wider mb-3"
                style={{ color: previewTheme.colors.textMuted }}
              >
                Color Palette
              </p>
              <div className="grid grid-cols-2 gap-3">
                {previewPalettes?.map((palette) => (
                  <PalettePreview
                    key={palette.id}
                    palette={palette}
                    isActive={previewPalette?.id === palette.id}
                    onClick={() => setPreviewPalette(palette)}
                    theme={previewTheme}
                  />
                ))}
              </div>
            </div>

            {/* Preview card */}
            <div className="mb-6">
              <p 
                className="text-xs uppercase tracking-wider mb-3"
                style={{ color: previewTheme.colors.textMuted }}
              >
                Preview
              </p>
              <motion.div
                layout
                className={`p-5 ${previewTheme.visual.cardRadius}`}
                style={{ 
                  background: previewTheme.colors.surface,
                  border: `1px solid ${previewTheme.colors.border}`,
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <motion.div 
                    className={`w-10 h-10 ${previewTheme.visual.borderRadius} flex items-center justify-center`}
                    style={{ background: previewPalette?.primary || previewTheme.colors.primary }}
                    layoutId="preview-icon"
                  >
                    <Sparkles size={18} className="text-white" />
                  </motion.div>
                  <div>
                    <p 
                      className="font-medium font-outfit"
                      style={{ color: previewTheme.colors.textPrimary }}
                    >
                      Sample Card
                    </p>
                    <p 
                      className="text-sm"
                      style={{ color: previewTheme.colors.textSecondary }}
                    >
                      This is how content looks
                    </p>
                  </div>
                </div>
                <motion.button
                  className={`w-full py-3 ${previewTheme.visual.buttonRadius} font-medium text-sm`}
                  style={{ 
                    background: `linear-gradient(135deg, ${previewPalette?.primary || previewTheme.colors.primary}, ${previewPalette?.accent || previewTheme.colors.accent})`,
                    color: '#fff',
                  }}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  Button Preview
                </motion.button>
              </motion.div>
            </div>

            {/* "Does this feel right?" section */}
            {hasChanges && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-4"
              >
                <p 
                  className="text-sm font-medium"
                  style={{ color: previewTheme.colors.textSecondary }}
                >
                  Does this feel right for you?
                </p>
              </motion.div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              {hasChanges && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={handleReset}
                  className={`px-4 py-3 ${previewTheme.visual.buttonRadius} font-medium flex items-center gap-2`}
                  style={{ 
                    background: `${previewTheme.colors.textMuted}20`,
                    color: previewTheme.colors.textSecondary,
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <RefreshCw size={16} />
                  Reset
                </motion.button>
              )}
              <motion.button
                onClick={hasChanges ? handleApply : onClose}
                className={`flex-1 py-3 ${previewTheme.visual.buttonRadius} font-medium flex items-center justify-center gap-2`}
                style={{ 
                  background: `linear-gradient(135deg, ${previewPalette?.primary || previewTheme.colors.primary}, ${previewPalette?.accent || previewTheme.colors.accent})`,
                  color: '#fff',
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="theme-apply-btn"
              >
                {hasChanges ? (
                  <>
                    <Check size={16} />
                    Apply Changes
                  </>
                ) : (
                  'Done'
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TeenThemeSelector;
